/**
 * TRAC-279: Hook for reading experiment variant assignments.
 *
 * TRAC-497: Reads from Zustand store instead of polling localStorage.
 * RealChatAgentService writes experiment assignments to the store
 * when the session ID is created, so React components get notified
 * reactively without setInterval polling.
 *
 * Overrides (dev panel, URL ?exp=) still take priority and are
 * checked synchronously. localStorage is used as a fallback for
 * initial load in case the store hasn't been populated yet.
 */

import { useEffect, useRef, useMemo } from 'react';
import { parseExperimentCode } from '../utils/experimentParser';
import { useChatStore } from '../stores/chatStore';
import { logger } from '../utils/logger';
import { isProdBuild } from '../services/handoffProviderOverride';

const STORAGE_KEY = 'traci_agent_session';
const EXP_OVERRIDE_KEY = 'traci_experiment_override';
const DEV_EXP_OVERRIDE_KEY = 'traci_dev_experiment_override';

/**
 * The Dev Settings experiment override, or null. A prod build ignores it
 * (TRACI-933): the drawer is gone there, so a leftover value could never be cleared.
 */
function readDevOverride(): string | null {
  if (isProdBuild()) return null;
  return localStorage.getItem(DEV_EXP_OVERRIDE_KEY);
}

/**
 * Parse "A1B2" or "a1b2" format into { A: 1, B: 2 }.
 * Always normalizes keys to uppercase for consistent lookups.
 */
function parseOverrideCode(code: string): Record<string, number> {
  const assignments: Record<string, number> = {};
  const pattern = /([A-Za-z])(\d+)/g;
  for (const match of code.matchAll(pattern)) {
    assignments[match[1].toUpperCase()] = parseInt(match[2], 10);
  }
  return assignments;
}

/**
 * Read experiment overrides.
 * Priority: dev panel override > URL ?exp= / sessionStorage > (none).
 */
function getOverrideAssignments(): Record<string, number> {
  if (typeof window === 'undefined') return {};
  // Dev panel override has highest priority (TRAC-291)
  const devOverride = readDevOverride();
  if (devOverride) return parseOverrideCode(devOverride);
  // URL param / session-persisted override
  const urlOverride = new URLSearchParams(window.location.search).get('exp');
  const override = urlOverride || sessionStorage.getItem(EXP_OVERRIDE_KEY);
  if (override) return parseOverrideCode(override);
  return {};
}

/**
 * Read the session ID from localStorage, handling the JSON wrapper.
 * RealChatAgentService stores it as JSON.stringify({ sessionId }).
 */
function getSessionIdFromStorage(): string | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    const data = JSON.parse(stored);
    return data.sessionId ?? null;
  } catch {
    return stored; // fallback: treat as raw string
  }
}

/**
 * Returns the experiment variant number for a given feature letter.
 * Returns null if no session exists yet or the feature isn't in the experiment.
 *
 * @param feature - Single letter feature code (e.g., "A", "B")
 * @returns Variant number (1-indexed) or null
 */
export function useExperiment(feature: string): number | null {
  // Subscribe to store experiments reactively
  const storeExperiments = useChatStore((s) => s.experiments);

  // Merge: overrides > store > localStorage fallback
  const resolved = useMemo(() => {
    const overrides = getOverrideAssignments();
    if (overrides[feature] != null) return overrides[feature];
    if (storeExperiments[feature] != null) return storeExperiments[feature];
    // localStorage fallback for initial load before store is populated
    const sessionId = getSessionIdFromStorage();
    if (sessionId) {
      const assignments = parseExperimentCode(sessionId);
      if (assignments[feature] != null) return assignments[feature];
    }
    return null;
  }, [feature, storeExperiments]);

  return resolved;
}

interface UseExperimentsResult {
  experiments: Record<string, number>;
  isOverride: boolean;
}

/**
 * Returns all experiment assignments as a Record<string, number>,
 * plus whether the values come from a dev panel override.
 * Returns empty experiments if no session exists yet.
 */
export function useExperiments(): UseExperimentsResult {
  const loggedRef = useRef(false);

  // Subscribe to store experiments reactively
  const storeExperiments = useChatStore((s) => s.experiments);

  // Merge: overrides > store > localStorage fallback
  const { assignments, isOverride } = useMemo(() => {
    const overrides = getOverrideAssignments();
    const hasOverrides = Object.keys(overrides).length > 0;
    const hasDevOverride = !!readDevOverride();

    // Base assignments: store > localStorage fallback
    let base = storeExperiments;
    if (Object.keys(base).length === 0) {
      const sessionId = getSessionIdFromStorage();
      if (sessionId) {
        base = parseExperimentCode(sessionId);
      }
    }

    const merged = hasOverrides ? { ...base, ...overrides } : base;
    return { assignments: merged, isOverride: hasDevOverride };
  }, [storeExperiments]);

  // Log once when assignments become available
  useEffect(() => {
    if (!loggedRef.current && Object.keys(assignments).length > 0) {
      const code = Object.entries(assignments).map(([f, v]) => `${f}${v}`).join('-');
      const source = isOverride ? 'override' : 'session';
      logger.info(`[EXPERIMENT] Active assignments: ${code} (${source})`);
      loggedRef.current = true;
    }
  }, [assignments, isOverride]);

  return { experiments: assignments, isOverride };
}
